import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Grid, Tooltip, Typography } from "@mui/material";
import {
  activateDeactivateVendorById,
  getVendorDetailsById,
  getSubProductsByLob,
} from "../../Services/vendorManagement.service";
import EditIcon from "@mui/icons-material/Edit";
import DomainDisabledIcon from '@mui/icons-material/DomainDisabled';
import DomainAddIcon from '@mui/icons-material/DomainAdd';
import { isLoadingStateReducer } from "../../Redux/Slice/loginSlice";
import useToaster from "../../customHook/useToaster";
import styles from "./VendorManagement.module.css";
import { useDispatch } from "react-redux";
import CommonModal from "../../CommonComponents/Modal/CommonModal";
import EditVendorDetails from "./EditVendorDetails";
import ServiceableLocations from "./ServiceableLocations";
import MuiTab from "../../CommonComponents/Tab/MuiTab";
import { capitalize } from "lodash";

const TabPanel = ({ children, value, index }) => {
  if (value !== index) return null;
  return <Box role="tabpanel">{children}</Box>;
};

const vendorDetailTabs = [{ label: "Vendor Details" }, { label: "Serviceable Locations" }];

const DisplayVendorDetails = ({ vendorId, open, onClose, isLoading }) => {
  const { showToast } = useToaster();
  const dispatch = useDispatch();
  const [vendorDetails, setVendorDetails] = useState();
  const [isEditing, setIsEditing] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const [fetchedSubProducts, setFetchedSubProducts] = useState([]);

  const getVendorDetails = useCallback(async () => {
    if (!vendorId || !open) return;
    try {
      dispatch(isLoadingStateReducer(true));
      const res = await getVendorDetailsById(vendorId);
      if (res?.success) {
        const vData = res.data;
        let lobId = null;
        if (vData.is_motor === 1 || vData.is_motor === "1") {
          lobId = 1; // Motor
        } else if (vData.is_health === 1 || vData.is_health === "1") {
          lobId = 2; // Health
        }
        if (lobId) {
          try {
            const subRes = await getSubProductsByLob(lobId);
            if (subRes?.success) {
              setFetchedSubProducts(subRes.data || []);
            }
          } catch (e) {
            console.error("Failed to fetch sub-products API", e);
          }
        }
        setVendorDetails(vData);
        dispatch(isLoadingStateReducer(false));
      } else {
        dispatch(isLoadingStateReducer(false));
        showToast(res?.message || "Unable to fetch vendor details.", "error", true);
      }
    } catch (err) {
      dispatch(isLoadingStateReducer(false));
      showToast(
        err?.response?.data?.message || "Unable to fetch vendor details.",
        "error",
        true
      );
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (!open || !vendorId) {
      setVendorDetails(undefined);
      setIsEditing(false);
      setActiveTab(0);
      setFetchedSubProducts([]);
      return;
    }
    getVendorDetails();
  }, [open, getVendorDetails]);

  const lobLabel = useMemo(() => {
    if (!vendorDetails) return "N/A";
    const lobs = [];
    if (vendorDetails.is_health === 1 || vendorDetails.is_health === "1") {
      lobs.push("Health");
    }
    if (vendorDetails.is_motor === 1 || vendorDetails.is_motor === "1") {
      lobs.push("Motor");
    }
    return lobs.length ? lobs.join(", ") : "N/A";
  }, [vendorDetails]);

  const capacityValue = useMemo(() => {
    if (!vendorDetails) return "N/A";
    const capacities = [];
    if (vendorDetails.is_health === 1 || vendorDetails.is_health === "1") {
      capacities.push(`Health: ${vendorDetails.capacity_health ?? "N/A"}`);
    }
    if (vendorDetails.is_motor === 1 || vendorDetails.is_motor === "1") {
      capacities.push(`Motor: ${vendorDetails.capacity_motor ?? "N/A"}`);
    }
    return capacities.length ? capacities.join(", ") : "N/A";
  }, [vendorDetails]);

  const highValueProducts = useMemo(() => {
    if (!vendorDetails) return "N/A";
    const mapping = {
      od_hv: "OD HV",
      tp_hv: "TP HV",
      health_hv: "Health HV",
    };
    const values = Object.entries(mapping)
      .filter(([key]) => vendorDetails[key] === 1 || vendorDetails[key] === "1")
      .map(([, label]) => label);
    return values.length ? values.join(", ") : "N/A";
  }, [vendorDetails]);

  const subProducts = useMemo(() => {
    if (!vendorDetails?.sub_product_id?.length || !fetchedSubProducts.length) return "N/A";

    const selectedSubProducts = fetchedSubProducts
      .filter((sp) => vendorDetails.sub_product_id.includes(sp.sub_product_id))
      .map((sp) => sp.sub_product_name);

    return selectedSubProducts.length ? selectedSubProducts.join(", ") : "N/A";
  }, [vendorDetails, fetchedSubProducts]);

  const displayValue = (value) => {
    if (value === null || value === undefined || value === "") return "N/A";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) return value.length ? value.join(", ") : "N/A";
    return value;
  };

  const isVendorActive = useMemo(() => vendorDetails?.isActive === true, [vendorDetails]);

  const handleEditToggle = () => {
    setIsEditing((prev) => !prev);
  };

  const handleStatusToggle = async () => {
    if (!vendorId) return;
    try {
      setIsStatusUpdating(true);
      const nextStatus = isVendorActive ? false : true;
      const res = await activateDeactivateVendorById(vendorId, nextStatus);
      if (res?.success) {
        showToast(
          res?.message || `Vendor ${isVendorActive ? "disabled" : "enabled"} successfully.`,
          "success",
          true
        );
        await getVendorDetails();
      } else {
        showToast(res?.message || "Unable to update vendor status.", "error", true);
      }
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Unable to update vendor status.", "error",
        true
      );
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const handleTabActiveChange = (newValue) => {
    if (isEditing) return;
    setActiveTab(newValue);
  };

  if (!open || !vendorDetails) return null;

  return (
    <CommonModal open={open} onClose={onClose} width="96vw" maxWidth="980px">
      <Box className={styles.detailModalBody}>
        <Box className={styles.detailHeader}>
          <Box>
            <Typography variant="h5" className={styles.detailTitle}>
              {vendorDetails.company_name || "Vendor Details"}
            </Typography>
            <Typography variant="body2" className={styles.detailSubtitle}>
              Complete profile information for the {vendorDetails?.company_name}.
            </Typography>
          </Box>
          {activeTab === 0 && (
            <div className={styles["actionIconContainer"]}>
              <Tooltip
                title="Edit Vendor"
                arrow placement="bottom"
                classes={{ tooltip: styles["customTooltip"] }}
              >
                {
                  isVendorActive && <EditIcon
                    className={styles.editIconSvg}
                    onClick={handleEditToggle}
                    disabled={isStatusUpdating}
                  />
                }
              </Tooltip>
              {isVendorActive ? (
                <Tooltip
                  title="Disable Vendor"
                  arrow placement="bottom"
                  classes={{ tooltip: styles["customTooltip"] }}
                >
                  {
                    !isEditing && <DomainDisabledIcon
                      className={styles.editIconSvg}
                      onClick={handleStatusToggle}
                      disabled={isStatusUpdating}
                    />
                  }
                </Tooltip>
              ) : (
                <Tooltip
                  title="Enable Vendor"
                  arrow placement="bottom"
                  classes={{ tooltip: styles["customTooltip"] }}
                >
                    {
                      !isEditing &&
                      <DomainAddIcon
                        className={styles.editIconSvg}
                        onClick={handleStatusToggle}
                        disabled={isStatusUpdating}
                      />
                    }
                </Tooltip>
              )}
            </div>
          )}
        </Box>
        <Box className={`${styles["tabWrapper"]} ${isEditing ? styles["disabledTabWrapper"] : ""}`}>
          <MuiTab
            tabs={vendorDetailTabs}
            activeTab={activeTab}
            setActiveTab={handleTabActiveChange}
            variant="boxed"
            fontSize="12px"
          />
        </Box>
        <TabPanel value={activeTab} index={0}>
          {isEditing ? (
            <EditVendorDetails
              vendorId={vendorId}
              vendorDetails={vendorDetails}
              onCancel={() => setIsEditing(false)}
              onSaved={async () => {
                await getVendorDetails();
                setIsEditing(false);
              }}
            />
          ) : (
            <Grid container spacing={2}>
              {[
                { label: "Company Name", value: vendorDetails.company_name },
                { label: "GC Code", value: vendorDetails.gc_code },
                { label: "LOB", value: lobLabel },
                { label: "PAN Number", value: vendorDetails.pan_number },
                { label: "GST Number", value: vendorDetails.gst_number },
                { label: "State", value: capitalize(vendorDetails.state) },
                { label: "City", value: capitalize(vendorDetails.city) },
                {
                  label: "Status",
                  value:
                    vendorDetails.isActive === true || vendorDetails.isActive === "true"
                      ? "Active"
                      : "Inactive",
                },
                { label: "Club", value: vendorDetails.club },
                { label: "Company Size", value: vendorDetails.company_size },
                {
                  label: "Specialised",
                  value:
                    vendorDetails.specialised === 1 || vendorDetails.specialised === "1"
                      ? "Yes"
                      : "No",
                },
                { label: "High Value", value: highValueProducts },
                { label: "Capacity", value: capacityValue },
                
                !(vendorDetails.is_health === 1 || vendorDetails.is_health === "1") && {
                  label: "Sub Product's",
                  value: subProducts
                },
                
                { label: "Employee Name", value: vendorDetails.employee_name },
                { label: "Employee Mobile", value: vendorDetails.employee_mobile },
                { label: "Employee Email", value: vendorDetails.employee_email },
              ]
              .filter(Boolean)
              .map((item) => (
                <Grid item xs={12} sm={6} key={item.label}>
                  <Box className={styles.detailItem}>
                    <Typography className={styles.detailLabel}>{item.label}</Typography>
                    <Typography className={styles.detailValue}>{displayValue(item.value)}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <ServiceableLocations
              vendorId={vendorId}
              vendorDetails={vendorDetails}
              active={activeTab === 1}
              disabled={isEditing}
            />
          </TabPanel>
      </Box>
    </CommonModal>
    );
};

export default DisplayVendorDetails;